
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { 
  fetchSuggestionsFromSupabase, 
  fetchSuggestionsSimple,
  fetchAllSuggestionsRaw
} from '@/utils/suggestions';

type Suggestion = Tables<'suggestions'>;
type FetchMethod = 'standard' | 'simple' | 'rpc';

export const useSuggestionsData = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [fetchMethod, setFetchMethod] = useState<FetchMethod>('standard');

  const fetchSuggestions = useCallback(async () => {
    setIsRefreshing(true);
    setIsLoading(true);
    
    try {
      console.log('Fetching suggestions using method:', fetchMethod);
      
      // Try different fetch methods based on the selected method
      if (fetchMethod === 'standard') {
        await fetchSuggestionsFromSupabase(setSuggestions);
      } else if (fetchMethod === 'simple') {
        await fetchSuggestionsSimple(setSuggestions);
      } else if (fetchMethod === 'rpc') {
        await fetchAllSuggestionsRaw(setSuggestions);
      }
    } catch (error) {
      console.error('Error in fetchSuggestions:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fetchMethod]);

  useEffect(() => {
    fetchSuggestions();

    // Set up realtime subscription with improved error handling
    const suggestionChannel = supabase
      .channel('suggestions-channel')
      .on(
        'postgres_changes', 
        { 
          event: '*',  // Listen for all events (INSERT, UPDATE, DELETE)
          schema: 'public', 
          table: 'suggestions' 
        },
        (payload) => {
          console.log('Received suggestion change via realtime:', payload);
          setRealtimeConnected(true);
          
          if (payload.eventType === 'INSERT' && payload.new) {
            setSuggestions((prev) => [payload.new as Suggestion, ...prev]);
            toast.info('Nytt tips mottatt!');
          } else if (payload.eventType === 'DELETE' && payload.old) {
            console.log('Realtime DELETE event detected for ID:', payload.old.id);
            // Remove deleted suggestion from state
            setSuggestions((prev) => {
              const filtered = prev.filter(s => s.id !== payload.old.id);
              console.log(`Filtered suggestions from ${prev.length} to ${filtered.length}`);
              return filtered;
            });
            toast.info('Et tips ble slettet');
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to real-time suggestions');
          setRealtimeConnected(true);
        } else {
          setRealtimeConnected(false);
        }
      });

    return () => {
      console.log('Cleaning up realtime subscription');
      supabase.removeChannel(suggestionChannel);
    };
  }, [fetchMethod, fetchSuggestions]); 

  useEffect(() => {
    console.log('Suggestions state updated, count:', suggestions.length);
  }, [suggestions]);
  
  const changeFetchMethod = (method: FetchMethod) => {
    setFetchMethod(method);
    // Trigger a fetch with the new method soon
    setTimeout(() => fetchSuggestions(), 100);
  };
  
  const handleManualCheck = async () => {
    setIsLoading(true);
    try {
      // Use the RPC method for manual check
      await fetchAllSuggestionsRaw(setSuggestions);
      toast.info(`Manuell sjekk fullført`);
    } catch (error) {
      console.error('Manual check error:', error);
      toast.error('Feil ved manuell sjekk');
    } finally {
      setIsLoading(false);
    }
  };
  
  const suggestionsPreview = suggestions.length > 0 
    ? JSON.stringify(suggestions.slice(0, 1)).substring(0, 100) + '...' 
    : 'No data';

  return {
    suggestions,
    setSuggestions,
    isLoading,
    isRefreshing,
    realtimeConnected,
    fetchMethod,
    fetchSuggestions,
    changeFetchMethod,
    handleManualCheck,
    suggestionsPreview
  };
};
